import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { 
  Upload, 
  Play, 
  CheckCircle2, 
  AlertTriangle,
  Clock,
  Loader2,
  FileText,
  BarChart3,
  Brain
} from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { supabase } from '@/integrations/supabase/client'

interface PrecheckResult {
  status: 'ok' | 'error' | 'warning'
  rows_total_seen: number
  rows_sampled: number
  mapping: Record<string, string>
  normalization_report: {
    valid_dates_percent: number
    valid_amounts_percent: number
    inferred_due_dates_count: number
    unique_events: string[]
  }
  warnings: string[]
  errors: string[]
  sample_normalized_rows: any[]
}

interface DeterministicResult {
  findings: Array<{
    id: string
    rule: string
    transaction_id: string
    key_values: Record<string, any>
  }>
  rules_status: Record<string, 'executed' | 'skipped'>
  summary: {
    total_findings: number
    by_rule: Record<string, number>
  }
}

interface AIResult {
  findings: Array<{
    id: string
    rule: string
    priority: 'low' | 'medium' | 'high'
    explanation: string
    context: Record<string, any>
  }>
  processing_stats: {
    total_processed: number
    mini_processed: number
    premium_processed: number
  }
  ai_status: 'success' | 'degraded' | 'error'
  status_message: string
}

interface PipelineState {
  step: 'upload' | 'precheck' | 'deterministic' | 'ai' | 'complete'
  precheck?: PrecheckResult
  deterministic?: DeterministicResult
  ai?: AIResult
  csvContent?: string
  fileName?: string
}

export function TaxAccountPipeline() {
  const { t } = useTranslation('common')
  const { toast } = useToast()
  const [state, setState] = useState<PipelineState>({ step: 'upload' })
  const [loading, setLoading] = useState(false)

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast({
        title: "Felaktigt filformat",
        description: "Endast CSV-filer stöds",
        variant: "destructive"
      })
      return
    }

    const content = await file.text()
    setState({
      step: 'precheck',
      csvContent: content,
      fileName: file.name
    })

    toast({
      title: "Fil uppladdad",
      description: `${file.name} är redo för analys`
    })
  }

  const runPrecheck = async () => {
    if (!state.csvContent) return

    setLoading(true)
    try {
      const response = await supabase.functions.invoke('testlab-tax-account-pipeline', {
        body: {
          step: 'precheck',
          csv_content: state.csvContent,
          file_name: state.fileName
        }
      })

      if (response.error) throw response.error

      setState(prev => ({
        ...prev,
        precheck: response.data,
        step: prev.precheck?.status === 'error' ? 'precheck' : 'deterministic'
      }))

      toast({
        title: "Precheck slutförd",
        description: `Analyserat ${response.data.rows_sampled} av ${response.data.rows_total_seen} rader`
      })
    } catch (error) {
      toast({
        title: "Precheck misslyckades",
        description: error instanceof Error ? error.message : "Okänt fel",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const runDeterministicAnalysis = async () => {
    if (!state.csvContent || !state.precheck) return

    setLoading(true)
    try {
      const response = await supabase.functions.invoke('testlab-tax-account-pipeline', {
        body: {
          step: 'deterministic',
          csv_content: state.csvContent,
          mapping: state.precheck.mapping
        }
      })

      if (response.error) throw response.error

      setState(prev => ({
        ...prev,
        deterministic: response.data,
        step: 'ai'
      }))

      toast({
        title: "Deterministisk analys slutförd",
        description: `Hittade ${response.data?.summary?.total_findings ?? (response.data?.findings?.length ?? 0)} avvikelser`
      })
    } catch (error) {
      toast({
        title: "Deterministisk analys misslyckades",
        description: error instanceof Error ? error.message : "Okänt fel",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  const runAIAnalysis = async () => {
    if (!state.deterministic) return

    setLoading(true)
    try {
      const response = await supabase.functions.invoke('testlab-tax-account-pipeline', {
        body: {
          step: 'ai',
          findings: state.deterministic.findings,
          csv_content: state.csvContent,
          mapping: state.precheck?.mapping,
          top_k: 30
        }
      })

      if (response.error) throw response.error

      setState(prev => ({
        ...prev,
        ai: response.data,
        step: 'complete'
      }))

      toast({
        title: "AI-analys slutförd",
        description: `Prioriterade och förklarade ${response.data.processing_stats.total_processed} fynd`
      })
    } catch (error) {
      toast({
        title: "AI-analys misslyckades",
        description: error instanceof Error ? error.message : "Okänt fel",
        variant: "destructive"
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Pipeline Progress */}
        <PipelineStep 
          title="Fil upload"
          icon={<Upload className="w-4 h-4" />}
          status={state.step === 'upload' ? 'active' : state.csvContent ? 'complete' : 'pending'}
        />
        <PipelineStep 
          title="Precheck"
          icon={<FileText className="w-4 h-4" />}
          status={state.step === 'precheck' ? 'active' : state.precheck ? 'complete' : 'pending'}
        />
        <PipelineStep 
          title="Deterministisk"
          icon={<BarChart3 className="w-4 h-4" />}
          status={state.step === 'deterministic' ? 'active' : state.deterministic ? 'complete' : 'pending'}
        />
        <PipelineStep 
          title="AI-analys"
          icon={<Brain className="w-4 h-4" />}
          status={state.step === 'ai' ? 'active' : state.ai ? 'complete' : 'pending'}
        />
      </div>

      {/* Step 1: Upload */}
      {state.step === 'upload' && (
        <Card>
          <CardHeader>
            <CardTitle>Steg 1: Ladda upp CSV-fil</CardTitle>
            <CardDescription>
              Välj en skattekontofil för analys (upp till 1M+ rader stöds)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload">
              <Button variant="outline" className="cursor-pointer" asChild>
                <span>
                  <Upload className="w-4 h-4 mr-2" />
                  Välj CSV-fil
                </span>
              </Button>
            </label>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Precheck */}
      {(state.step === 'precheck' || state.precheck) && (
        <Card>
          <CardHeader>
            <CardTitle>Steg 2: Dry-run / Precheck</CardTitle>
            <CardDescription>
              Snabb parsing av header + första 5000 rader för mappning och validering
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!state.precheck && (
              <Button 
                onClick={runPrecheck} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Kör precheck...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Kör Precheck
                  </>
                )}
              </Button>
            )}

            {state.precheck && (
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  {state.precheck.status === 'ok' && <CheckCircle2 className="w-5 h-5 text-green-500" />}
                  {state.precheck.status === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                  {state.precheck.status === 'error' && <AlertTriangle className="w-5 h-5 text-red-500" />}
                  <span className="font-medium">
                    Status: {state.precheck.status.toUpperCase()}
                  </span>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <div className="text-muted-foreground">Totalt rader</div>
                    <div className="font-medium">{(state.precheck.rows_total_seen || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Samplade</div>
                    <div className="font-medium">{(state.precheck.rows_sampled || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Giltiga datum</div>
                    <div className="font-medium">{state.precheck.normalization_report?.valid_dates_percent?.toFixed(1) ?? 'N/A'}%</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Giltiga belopp</div>
                    <div className="font-medium">{state.precheck.normalization_report?.valid_amounts_percent?.toFixed(1) ?? 'N/A'}%</div>
                  </div>
                </div>

                {(state.precheck.warnings?.length || 0) > 0 && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Varningar:</strong>
                      <ul className="list-disc list-inside mt-2">
                        {state.precheck.warnings?.map((warning, i) => (
                          <li key={i}>{warning}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}

                {(state.precheck.errors?.length || 0) > 0 && (
                  <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Fel:</strong>
                      <ul className="list-disc list-inside mt-2">
                        {state.precheck.errors?.map((error, i) => (
                          <li key={i}>{error}</li>
                        ))}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 3: Deterministic Analysis */}
      {(state.step === 'deterministic' || state.deterministic) && (
        <Card>
          <CardHeader>
            <CardTitle>Steg 3: Deterministisk analys</CardTitle>
            <CardDescription>
              Kör deterministiska regler mot hela filen
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!state.deterministic && state.precheck?.status !== 'error' && (
              <Button 
                onClick={runDeterministicAnalysis} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Analyserar...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Kör deterministiska regler
                  </>
                )}
              </Button>
            )}

            {state.deterministic && (
              <div className="space-y-4">
                <div className="text-lg font-medium">
                  Hittade {(state.deterministic?.summary?.total_findings ?? state.deterministic?.findings?.length ?? 0)} avvikelser
                </div>

                <div className="grid gap-2">
                  {Object.entries(state.deterministic?.summary?.by_rule || {}).map(([rule, count]) => (
                    <div key={rule} className="flex justify-between items-center p-3 border rounded">
                      <span>{rule}</span>
                      <Badge variant={count > 0 ? "default" : "secondary"}>
                        {count} fynd
                      </Badge>
                    </div>
                  ))}
                </div>

                <div className="space-y-2">
                  <h4 className="font-medium">Regelstatus:</h4>
                  {Object.entries(state.deterministic?.rules_status || {}).map(([rule, status]) => (
                    <div key={rule} className="flex justify-between items-center">
                      <span className="text-sm">{rule}</span>
                      <Badge variant={status === 'executed' ? "default" : "secondary"}>
                        {status === 'executed' ? 'Kördes' : 'Hoppades över'}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Step 4: AI Analysis */}
      {(state.step === 'ai' || state.ai) && (
        <Card>
          <CardHeader>
            <CardTitle>Steg 4: AI-prioritering & förklaring</CardTitle>
            <CardDescription>
              AI prioriterar och förklarar findings med kontext
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!state.ai && state.deterministic && (
              <Button 
                onClick={runAIAnalysis} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    AI analyserar...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Förklara findings
                  </>
                )}
              </Button>
            )}

            {state.ai && (
              <div className="space-y-4">
                {state.ai.ai_status === 'degraded' && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>Degraderat läge:</strong> {state.ai.status_message}
                      <br />
                      Mini-modellen användes för alla findings. Premium-modellen var otillgänglig.
                    </AlertDescription>
                  </Alert>
                )}
                <div className="text-lg font-medium">
                  AI-prioriterade findings ({(state.ai.findings?.length || 0)})
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Prioritet</TableHead>
                      <TableHead>Regel</TableHead>
                      <TableHead>Förklaring</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(state.ai.findings || []).slice(0, 10).map((finding) => (
                      <TableRow key={finding.id}>
                        <TableCell>
                          <Badge variant={
                            finding.priority === 'high' ? 'destructive' : 
                            finding.priority === 'medium' ? 'default' : 
                            'secondary'
                          }>
                            {finding.priority}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium">{finding.rule}</TableCell>
                        <TableCell className="max-w-md truncate">
                          {finding.explanation}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <div className="text-sm text-muted-foreground">
                  Bearbetade {state.ai.processing_stats.total_processed} fynd 
                  (Mini: {state.ai.processing_stats.mini_processed}, Premium: {state.ai.processing_stats.premium_processed})
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  )
}

interface PipelineStepProps {
  title: string
  icon: React.ReactNode
  status: 'pending' | 'active' | 'complete'
}

function PipelineStep({ title, icon, status }: PipelineStepProps) {
  return (
    <div className={`flex items-center gap-2 p-3 border rounded ${
      status === 'active' ? 'border-primary bg-primary/5' :
      status === 'complete' ? 'border-green-500 bg-green-50' :
      'border-muted'
    }`}>
      <div className={`p-1 rounded ${
        status === 'active' ? 'text-primary' :
        status === 'complete' ? 'text-green-500' :
        'text-muted-foreground'
      }`}>
        {status === 'complete' ? <CheckCircle2 className="w-4 h-4" /> : icon}
      </div>
      <div>
        <div className={`text-sm font-medium ${
          status === 'active' ? 'text-primary' :
          status === 'complete' ? 'text-green-700' :
          'text-muted-foreground'
        }`}>
          {title}
        </div>
      </div>
    </div>
  )
}